import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { Image } from '@mantine/core';
import { getCachedImage } from '@/renderer/utils/imageCache';
import { resolveServerUrl } from '@/renderer/utils/session';
// Import fallback so bundler copies it to output
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import defaultPng from '../../../../assets/default.png';
const DEFAULT_FALLBACK = defaultPng || 'assets/default.png';
const SafeImage = ({ src, fallbackSrc = DEFAULT_FALLBACK, alt, lazy = true, rootMargin = '200px', onLoad: onLoadProp, onError: onErrorProp, onResolved, ...rest }) => {
    // Start from fallback to avoid blank state when rapidly refreshing
    const [source, setSource] = React.useState(fallbackSrc);
    const [inView, setInView] = React.useState(!lazy);
    const ref = React.useRef(null);
    const loadSeq = React.useRef(0);
    const readyToNotify = React.useRef(false);
    // Intersection observer for lazy load
    React.useEffect(() => {
        if (!lazy)
            return;
        const el = ref.current;
        if (!el)
            return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    setInView(true);
                    try {
                        io.disconnect();
                    }
                    catch { }
                }
            });
        }, { rootMargin });
        io.observe(el);
        return () => { try {
            io.disconnect();
        }
        catch { } };
    }, [lazy, rootMargin]);
    React.useEffect(() => {
        let cancelled = false;
        const seq = ++loadSeq.current;
        readyToNotify.current = false;
        (async () => {
            if (!inView) {
                // Keep showing fallback until in view
                setSource((prev) => prev || fallbackSrc);
                return;
            }
            if (!src || String(src).trim() === '') {
                setSource(fallbackSrc);
                readyToNotify.current = true;
                onResolved?.();
                return;
            }
            try {
                const resolved = resolveServerUrl(String(src)) || String(src);
                const cached = await getCachedImage(resolved);
                if (!cancelled && loadSeq.current === seq) {
                    setSource(cached || fallbackSrc);
                    readyToNotify.current = true;
                    onResolved?.();
                }
            }
            catch {
                if (!cancelled && loadSeq.current === seq) {
                    setSource(fallbackSrc);
                    readyToNotify.current = true;
                    onResolved?.();
                }
            }
        })();
        return () => { cancelled = true; };
    }, [src, fallbackSrc, inView]);
    return (_jsx("div", { ref: ref, style: { width: '100%', height: '100%' }, children: _jsx(Image, { src: source, alt: alt, loading: lazy ? 'lazy' : undefined, decoding: lazy ? 'async' : undefined, onLoad: (e) => { if (readyToNotify.current)
                onLoadProp?.(e); }, onError: (e) => { if (readyToNotify.current)
                onErrorProp?.(e); setSource((prev) => (prev !== fallbackSrc ? fallbackSrc : prev)); }, ...rest }) }));
};
export default React.memo(SafeImage);
